import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Field, getIn } from 'formik';
import Autosuggest from 'react-autosuggest';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import emptyFunction from 'common/util/emptyFunction';
import extractDateString from 'common/util/extractDateString';

import { isPDFFile } from 'client/lib/fileUploader';

import {
  transformStringToBoolean,
  getTextInputFieldAttributes,
} from 'client/helpers/formikHelpers';

import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrTooltip, {
  SR_TOOLTIP_POSITION_LEFT,
  SR_TOOLTIP_ICON_QUESTION_MARK,
  SR_TOOLTIP_POSITIONS,
  SR_TOOLTIP_BG_COLOR_TO_CLASSNAME_MAPPING,
} from 'client/components/SrTooltip';
import SrDatePicker from 'client/components/SrDatePicker';
import DateInput from 'client/components/DateInput';
import ExpirationDateInput from 'client/components/ExpirationDateInput';
import SrCheckboxList from 'client/components/SrCheckboxList';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrFilterCheckbox from 'client/components/SrFilterCheckbox';
import SrCheckbox from 'client/components/SrCheckbox';
import SrFileUploader from 'client/components/SrFileUploader';
import SrMultiFileUploader from 'client/components/SrMultiFileUploader';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import SrStarRating from 'client/components/SrStarRating';
import SrInfoOptionCheckboxList from 'client/components/SrInfoOptionCheckboxList/SrInfoOptionCheckboxList';
import SrFormFieldGoogleAddressAutoComplete from 'client/components/SrFormFieldGoogleAddressAutoComplete/SrFormFieldGoogleAddressAutoComplete';
import SrFormFieldTagList from 'client/components/SrFormFieldTagList/SrFormFieldTagList';
import SrEditTextbox, { SR_EDIT_TEXTBOX_INPUT_TYPE_TEXT } from 'client/components/SrEditTextbox';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrMarkdown from 'client/components/SrMarkdown';
import SrFormFieldActivationAutoComplete from 'client/components/SrFormFieldActivationAutoComplete/SrFormFieldActivationAutoComplete';
import SrFormFieldSmartyAddressAutoComplete from './SrFormFieldSmartyAddressAutoComplete/SrFormFieldSmartyAddressAutoComplete';

export const SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL = 'rentalApplicationFieldHorizontal';
export const SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED =
  'rentalApplicationFieldHorizontalAdjusted'; // eslint-disable-line max-len
export const SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL = 'rentalApplicationFieldVertical';
export const SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED =
  'rentalApplicationFieldVerticalAdjusted'; // eslint-disable-line max-len
export const SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_CHECKBOX =
  'rentalApplicationFieldHorizontalCheckbox'; // eslint-disable-line max-len
export const SR_FIELD_THEME_PARTNER_FORM = 'partnerForm';
export const SR_FIELD_THEME_PROPERTY_CONDITION_FORM = 'propertyConditionReportForm';
export const SR_FIELD_THEME_ORDER_UNINSTALL_FORM = 'orderUninstallForm';
export const SR_FIELD_THEME_ADMIN = 'adminTheme';
export const SR_FIELD_THEME_ADMIN_HORIZONTAL_SMALL = 'adminHorizontalSmall';
export const SR_FIELD_THEME_CHECKBOX_BLUE = 'checkboxBlue';
export const SR_FIELD_THEME_LONG_TEXTAREA = 'longTextarea';
export const SR_FIELD_THEME_ADOBE = 'adobe';
export const SR_FIELD_THEME_ADOBE_UTILITY_PROFIT = 'adobeUtilityProfit';
export const SR_FIELD_THEME_ADOBE_LONG_LABEL = 'adobeLongLabel';
export const SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE = 'adobeAssemblyLine';
export const SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE = 'adobeAssemblyLineOrange';
export const SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_RED = 'adobeAssemblyLineRed';
export const SR_FIELD_THEME_GREYCLIFF_DARK_BG = 'greycliffDarkBg';
export const SR_FIELD_THEME_WHITNEY = 'whitneyTheme';
export const SR_FIELD_THEME_PARTNER_FORM_ADOBE = 'partnerFormAdobe';
export const SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY = 'partnerFormAdobeGrey';
export const SR_FIELD_THEME_FORM_INPUT_BORDER_2X = 'inputBorder';

const SR_FIELD_THEMES = [
  SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
  SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
  SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
  SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
  SR_FIELD_THEME_PARTNER_FORM,
  SR_FIELD_THEME_ADMIN,
  SR_FIELD_THEME_ADMIN_HORIZONTAL_SMALL,
  SR_FIELD_THEME_CHECKBOX_BLUE,
  SR_FIELD_THEME_LONG_TEXTAREA,
  SR_FIELD_THEME_ADOBE,
  SR_FIELD_THEME_ADOBE_LONG_LABEL,
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE,
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE,
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_RED,
  SR_FIELD_THEME_GREYCLIFF_DARK_BG,
  SR_FIELD_THEME_WHITNEY,
  SR_FIELD_THEME_PARTNER_FORM_ADOBE,
  SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
  SR_FIELD_THEME_ORDER_UNINSTALL_FORM,
  SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY,
];

class SrFormikField extends Component {
  static propTypes = {
    formField: PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      properties: PropTypes.object,
      tooltipCopy: PropTypes.string,
      tooltipPosition: PropTypes.oneOf(SR_TOOLTIP_POSITIONS),
      tooltipBgColor: PropTypes.oneOf(Object.keys(SR_TOOLTIP_BG_COLOR_TO_CLASSNAME_MAPPING)),
      tooltipClassName: PropTypes.string,
    }).isRequired,
    formikBag: PropTypes.object,
    theme: PropTypes.oneOf(SR_FIELD_THEMES),
    margin: PropTypes.string,
    // TODO: Add support for a SrFormikField "disable" prop.
  };

  static contextTypes = {
    userAgent: PropTypes.object,
  };

  constructor(props) {
    super(props);
    if (props.formikBag && props.value) {
      throw new Error('SrFormikField accepts formikBag or value, but not both.');
    }
  }

  _onClickContainer = () => {
    const { formField } = this.props;
    const properties = formField.properties || {};

    // NOTE(@archr) This is required to focus the input element and put the cursor at the end of it
    if (properties.focusInputOnClickContainer && this.el && this.el.focus) {
      this.el.focus();
      const inputValue = this.el.value;
      this.el.value = '';
      this.el.value = inputValue;
    }
  };

  render() {
    const { formField, formikBag, margin } = this.props;
    const setFieldValue = formikBag ? formikBag.setFieldValue.bind(formikBag) : emptyFunction;
    const setFieldTouched = formikBag ? formikBag.setFieldTouched.bind(formikBag) : emptyFunction;
    const setFieldError = formikBag ? formikBag.setFieldError.bind(formikBag) : emptyFunction;

    const properties = formField.properties || {};
    const theme = this.props.theme || properties.theme;
    const fieldsContainerClassName = this.props.className || properties.className;
    const userAgent = this.context.userAgent;
    let showActiveClassName = false;
    let shouldWrapInLabel = false;

    const value =
      typeof this.props.value === 'undefined' && formikBag
        ? getIn(formikBag.values, formField.id)
        : this.props.value;

    let fieldDOM = null;
    switch (formField.type) {
      case 'name':
      case 'ssn':
      case 'zipCode':
      case 'verification_code':
      case 'phone':
      case 'card_number':
      case 'card_cvc':
      case 'number': {
        fieldDOM = (
          <Field
            innerRef={(el) => {
              this.el = el;
            }}
            {...getTextInputFieldAttributes({ formField, formikBag, userAgent })}
            validate={properties.validate}
          />
        );
        break;
      }
      case 'pin': {
        fieldDOM = (
          <Field
            innerRef={(el) => {
              this.el = el;
            }}
            {...getTextInputFieldAttributes({ formField, formikBag, userAgent })}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(event) => {
              // Only allow numeric input
              const numericValue = event.target.value.replace(/[^0-9]/g, '');
              setFieldValue(formField.id, numericValue);
            }}
            validate={properties.validate}
            value={value}
          />
        );
        break;
      }
      case 'short_text': {
        fieldDOM = (
          <Field
            innerRef={(el) => {
              this.el = el;
            }}
            {...getTextInputFieldAttributes({ formField, formikBag, userAgent })}
            validate={properties.validate}
            value={value}
          />
        );
        break;
      }
      case 'barcode_scan': {
        fieldDOM = (
          <SrFlex alignItems="center">
            <SrBox width="calc(100% - 50px)">
              <Field
                innerRef={(el) => {
                  this.el = el;
                }}
                {...getTextInputFieldAttributes({ formField, formikBag, userAgent })}
                validate={properties.validate}
                value={value}
              />
            </SrBox>
            <SrBox
              onClick={properties.onScanClick}
              cursor="pointer"
              backgroundImage="url(/images/scan-barcode.svg)"
              backgroundPosition="center"
              backgroundSize="contain"
              backgroundRepeat="no-repeat"
              width="40px"
              height="40px"
              marginLeft="4px"
              marginBottom="3px"
            />
          </SrFlex>
        );
        break;
      }
      case 'text_list': {
        fieldDOM = (
          <>
            <div>
              <SrEditTextbox
                initialValue=""
                onSave={(newValue) => {
                  // Adds the value only if the value didn't exist before.
                  const valueAlreadyExists = (value || []).some((_text) => {
                    const text = typeof _text === 'object' ? JSON.stringify(_text) : _text;
                    return (text || '').toLowerCase() === (newValue || '').toLowerCase();
                  });
                  if (!valueAlreadyExists && newValue) {
                    setFieldValue(formField.id, [...(value || []), newValue]);
                  }
                }}
                inputType={properties.inputType || SR_EDIT_TEXTBOX_INPUT_TYPE_TEXT}
                placeholder={properties.placeholder || 'Write here...'}
                srButtonProps={properties.addButtonProps}
                withSaveButton={properties.withSaveButton}
              />
            </div>
            {(value || []).map((_text, index) => {
              const text = typeof _text === 'object' ? JSON.stringify(_text) : _text;

              return (
                <div
                  key={text}
                  {...(properties.textListItemProps.containerStyle
                    ? { style: properties.textListItemProps.containerStyle }
                    : {})}
                >
                  {properties.textListItemProps?.itemTitle && (
                    <div
                      className={classnames(
                        lodashCompact([
                          'textListItemTitle',
                          properties.textListItemProps?.className,
                        ]),
                      )}
                    >
                      <span>
                        {properties.textListItemProps.itemTitle}
                        {index + 1}
                      </span>
                    </div>
                  )}
                  <div
                    className={classnames(
                      lodashCompact(['textListItem', properties.textListItemProps?.className]),
                    )}
                  >
                    <SrText font="greycliff" color="blueGreycliff" size="16">
                      {text}
                    </SrText>
                    <button
                      type="button"
                      onClick={() => {
                        const newValue = [...value];
                        newValue.splice(index, 1);
                        setFieldValue(formField.id, newValue);
                      }}
                    >
                      {properties.textListItemProps?.removeButtonChildren || 'Remove'}
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        );
        break;
      }
      case 'percentage': {
        const displayValue = typeof value === 'number' ? (value * 100).toFixed(0) : '';

        fieldDOM = (
          <Field
            {...getTextInputFieldAttributes({ formField, formikBag })}
            value={displayValue}
            validate={properties.validate}
            onChange={(event) => {
              const value = event.target.value;
              const percentageValue = value ? parseFloat(value) / 100 : null;
              if (typeof percentageValue !== 'number') {
                setFieldValue(formField.id, null);
              } else if (value[value.length - 1] !== '.' && !isNaN(percentageValue)) {
                setFieldValue(formField.id, percentageValue);
              }
            }}
          />
        );
        break;
      }
      case 'email': {
        fieldDOM = (
          <Field
            innerRef={(el) => {
              this.el = el;
            }}
            {...getTextInputFieldAttributes({ formField, formikBag, userAgent })}
            onChange={(event) => {
              const trimmedValue = (event.target.value || '').trim();
              setFieldValue(formField.id, trimmedValue);
            }}
            validate={properties.validate}
          />
        );
        break;
      }
      case 'currency': {
        fieldDOM = (
          <NumberFormat
            getInputRef={(el) => {
              this.el = el;
            }}
            {...getTextInputFieldAttributes({ formField, formikBag })}
            onChange={undefined}
            value={value}
            thousandSeparator
            prefix="$"
            type="tel"
            decimalScale={2}
            onValueChange={(values) => {
              // NOTE(@archr): Update from undefined to null when the field is cleaned
              // otherwise the field is not passed to the onSubmit function.
              const floatValue = values.floatValue === undefined ? null : values.floatValue;
              setFieldValue(formField.id, floatValue);
              if (properties.onChange) {
                properties.onChange(floatValue);
              }
            }}
          />
        );
        break;
      }
      case 'numberWithComma': {
        fieldDOM = (
          <NumberFormat
            {...getTextInputFieldAttributes({ formField, formikBag })}
            onChange={undefined}
            value={value}
            thousandSeparator
            type="tel"
            onValueChange={(values) => {
              setFieldValue(formField.id, values.floatValue);
              if (properties.onChange) {
                properties.onChange(values.floatValue);
              }
            }}
          />
        );
        break;
      }
      case 'password': {
        const fieldAttributes = {
          onKeyPress: properties.onKeyPress,
          autoFocus: properties.autoFocus,
          disabled: properties.disabled,
          className: properties.className,
          placeholder: properties.placeholder,
          type: 'password',
          pattern: properties.pattern,
        };

        if (properties.onChange) {
          fieldAttributes.onChange = properties.onChange;
        }

        fieldDOM = <Field name={formField.id} {...fieldAttributes} />;
        break;
      }
      case 'long_text': {
        const fieldAttributes = {
          onKeyPress: properties.onKeyPress,
          placeholder: properties.placeholder,
          className: 'sr-textarea value',
          component: 'textarea',
          autoFocus: properties.autoFocus,
          disabled: properties.disabled,
        };

        if (properties.onChange) {
          fieldAttributes.onChange = properties.onChange;
        }

        if (properties.onBlur) {
          fieldAttributes.onBlur = properties.onBlur;
        }

        if (properties.style) {
          fieldAttributes.style = properties.style;
        }

        fieldDOM = <Field name={formField.id} {...fieldAttributes} />;
        break;
      }
      case 'date':
      case 'date_picker': {
        fieldDOM = (
          <SrDatePicker
            formikBag={formikBag}
            className="datePicker"
            name={formField.id}
            skipOnChangeReRender={properties.skipOnChangeReRender}
            onChange={(momentInstance) => {
              let value = momentInstance?.toISOString ? momentInstance.toISOString() : null;

              if (properties.dateWithoutTime) {
                value = extractDateString(value);
              }

              setFieldValue(formField.id, value);

              if (value) {
                setFieldTouched(formField.id);
              }
              if (properties.onChange) {
                properties.onChange(value);
              }
            }}
            timeFormat={properties.timeFormat}
            dateFormat={properties.dateFormat || 'MM/DD/YYYY'}
            viewMode={properties.dateViewMode}
            value={value}
            placeholderValue={properties.placeholderValue}
            placeholder={properties.placeholder}
            isValidDate={properties.isValidDate}
            readOnly={properties.readOnly}
            disabled={properties.disabled}
            closeOnSelect
          />
        );
        break;
      }
      case 'week_picker': {
        fieldDOM = (
          <SrDatePicker
            formikBag={formikBag}
            className="datePicker"
            name={formField.id}
            skipOnChangeReRender={properties.skipOnChangeReRender}
            onChange={(momentInstance) => {
              if (!momentInstance) {
                setFieldValue(formField.id, null);
              } else if (momentInstance.toISOString) {
                setFieldValue(formField.id, momentInstance.toISOString());
                setFieldTouched(formField.id);
              }
              if (properties.onChange) {
                properties.onChange(momentInstance ? momentInstance.toISOString() : null);
              }
            }}
            timeFormat={properties.timeFormat}
            dateFormat={properties.dateFormat || 'MM/DD/YYYY'}
            viewMode={properties.dateViewMode}
            value={value}
            placeholderValue={properties.placeholderValue}
            placeholder={properties.placeholder}
            readOnly={properties.readOnly}
            isValidDate={properties.isValidDate}
            disabled={properties.disabled}
            closeOnSelect
            selection="weekly"
          />
        );
        break;
      }
      case 'date_input':
        fieldDOM = (
          <DateInput
            formFieldId={formField.id}
            onChange={(newValue) => {
              const newValueMoment = moment(newValue, 'YYYY-MM-DD');
              if (newValue.length === 10 && newValueMoment.isValid()) {
                if (properties.dateWithoutTime) {
                  setFieldValue(formField.id, newValue);
                } else {
                  setFieldValue(formField.id, newValueMoment.toISOString());
                }
              } else {
                setFieldValue(formField.id, null);
              }
            }}
            onBlur={() => {
              setFieldTouched(formField.id);
            }}
            value={value ? moment(value).format('YYYY-MM-DD') : ''}
            disabled={properties.disabled}
          />
        );
        break;
      case 'date_expiration_input':
        fieldDOM = (
          <ExpirationDateInput
            formFieldId={formField.id}
            onChange={(newValue) => {
              const newValueMoment = moment(newValue, 'MM-YY');

              if (newValueMoment.isValid()) {
                setFieldValue(formField.id, newValueMoment.toISOString());
              }
            }}
            onBlur={() => {
              setFieldTouched(formField.id);
            }}
            value={value ? moment(value).format('MM-YY') : ''}
            disabled={properties.disabled}
          />
        );
        break;
      case 'yes_no': {
        showActiveClassName = !!formikBag.values[formField.id];
        const labels = properties.labels || {};
        const oppositeBoolean = properties.oppositeBoolean || false;

        fieldDOM = (
          <SrCheckboxList
            optionList={[
              { value: !oppositeBoolean, label: labels.yes || 'Yes' },
              { value: oppositeBoolean, label: labels.no || 'No' },
            ]}
            onChange={(newValue) => {
              setFieldValue(formField.id, newValue);
              if (properties.onChange) {
                properties.onChange(newValue);
              }
            }}
            value={transformStringToBoolean(value)}
            autoFitWidth={properties.autoFitWidth}
            width={properties.width}
            gridGap={properties.gridGap}
            multipleSelection={false}
            disabled={properties.disabled}
            fieldType={SR_CHECKBOX_THEME_BOX}
            className="checkboxListBoolean"
          />
        );
        break;
      }
      case 'multiple_choice': {
        const fieldAttributes = {
          multipleSelection: properties.multipleSelection,
          useArrayValues: properties.useArrayValues,
          allowEmptyArray: properties.allowEmptyArray,
          toggleSingleSelection: properties.toggleSingleSelection,
          uncheckedValue: properties.uncheckedValue,
          theme: properties.theme,
          checkboxClassName: properties.checkboxClassName,
          className: properties.checkboxListClassName,
          selectedItemsFirst: properties.selectedItemsFirst,
          selectedItemsOnly: properties.selectedItemsOnly,
        };

        if (properties.onChange) {
          fieldAttributes.onChange = properties.onChange;
        }

        fieldDOM = (
          <SrCheckboxList
            optionList={properties.choices}
            value={value}
            onChange={(newValue) => {
              setFieldValue(formField.id, newValue);
            }}
            {...fieldAttributes}
            fieldType={properties.fieldType}
            autoFitWidth={properties.autoFitWidth}
            checkboxIconType={properties.checkboxIconType}
            columns={properties.columns}
            width={properties.width}
            gridGap={properties.gridGap}
          />
        );
        break;
      }
      case 'info_checkbox': {
        fieldDOM = (
          <SrInfoOptionCheckboxList
            optionList={properties.choices}
            value={value}
            onChange={(newValue) => {
              setFieldValue(formField.id, newValue);
            }}
          />
        );
        break;
      }

      case 'info_checkbox_multi': {
        fieldDOM = (
          <SrInfoOptionCheckboxList
            optionList={properties.choices}
            value={value}
            onChange={(selectedOption, initialValue = []) => {
              let newValue = selectedOption;

              if (Array.isArray(initialValue)) {
                newValue = initialValue.includes(selectedOption)
                  ? initialValue.filter((value) => value !== selectedOption)
                  : [...initialValue, selectedOption];
              }

              const setValue = () => {
                setFieldValue(formField.id, newValue);
              };

              if (properties.handleDuringChange) {
                properties.handleDuringChange(selectedOption, initialValue, setValue);
              } else {
                setValue();
              }
            }}
          />
        );
        break;
      }

      case 'select': {
        const fieldAttributes = {
          disabled: properties.disabled,
        };
        // TODO: Figure out if there's a non-WTF way of doing this without messing Field's native `onChange`.
        const optionalOnChange = {};
        if (properties.onChange) {
          optionalOnChange.onChange = properties.onChange;
        }

        if (properties.transformToBoolean) {
          optionalOnChange.onChange = (e) => {
            setFieldValue(formField.id, transformStringToBoolean(e.target.value));
          };
        }

        if (properties.transformToFloat) {
          optionalOnChange.onChange = (e) => {
            setFieldValue(formField.id, parseFloat(e.target.value));
          };
        }

        if (properties.transformToNumber) {
          optionalOnChange.onChange = (e) => {
            setFieldValue(formField.id, parseInt(e.target.value, 10));
          };
        }

        fieldDOM = (
          <Field
            component="select"
            name={formField.id}
            className={properties.className}
            {...optionalOnChange}
            {...fieldAttributes}
          >
            <option
              label={properties.placeholder || 'Choose'}
              value=""
              disabled={!properties.enableResetOption}
            />
            {(properties.choices || []).map((choice) => (
              <option label={choice.label} value={choice.value} key={choice.value}>
                {choice.label}
              </option>
            ))}
          </Field>
        );
        break;
      }
      case 'checkbox': {
        showActiveClassName = !!formikBag.values[formField.id];
        shouldWrapInLabel = true;
        fieldDOM = (
          <SrFilterCheckbox
            id={formField.id}
            disabled={properties.disabled}
            className={properties.className}
            value={!!value}
            onChange={(id, newValue) => {
              setFieldValue(id, newValue);
              if (properties.onChange) {
                properties.onChange(newValue);
              }
            }}
          />
        );
        break;
      }
      case 'simple_checkbox': {
        showActiveClassName = !!formikBag.values[formField.id];
        shouldWrapInLabel = true;
        fieldDOM = (
          <SrCheckbox
            className={properties.className}
            checked={!!value}
            onClick={() => {
              setFieldValue(formField.id, !value);
              if (properties.onChange) {
                properties.onChange(!value);
              }
            }}
          >
            {properties.children}
          </SrCheckbox>
        );
        break;
      }
      case 'markdown_checkbox': {
        showActiveClassName = !!formikBag.values[formField.id];
        shouldWrapInLabel = true;
        fieldDOM = (
          <>
            <SrMarkdown
              source={properties.markdown?.source}
              theme={properties.markdown?.theme}
              {...(properties.markdown?.styles || {})}
            />
            <SrCheckbox
              className={properties.className}
              checked={!!value}
              onClick={() => {
                setFieldValue(formField.id, !value);
                if (properties.onChange) {
                  properties.onChange(!value);
                }
              }}
            >
              {properties.children}
            </SrCheckbox>
          </>
        );
        break;
      }
      case 'file_single': {
        fieldDOM = (
          <SrFileUploader
            value={value}
            accept={properties.fileAccept}
            filePath={properties.filePath}
            placeholder={properties.placeholder}
            className={properties.className}
            checkmarks={properties.checkmarks}
            warning={properties.warning}
            isPrivate={properties.isPrivate}
            uploadAs={properties.uploadAs}
            minWidth={properties.minWidth}
            minHeight={properties.minHeight}
            onFileUploaded={({ s3FileUrl }) => {
              setFieldValue(formField.id, s3FileUrl);

              if (properties.onChange) {
                properties.onChange(s3FileUrl);
              }
            }}
            onFileRemoved={() => {
              setFieldValue(formField.id, null);
            }}
            successMessage={properties.successMessage}
            successMessageTextClassName={properties.successMessageTextClassName}
            downloadButtonLabel={properties.downloadButtonLabel}
            requirePDFDocs={properties.requirePDFDocs}
          />
        );
        break;
      }
      case 'file_multi': {
        fieldDOM = (
          <div>
            {Array.isArray(value) && value.length > 0 && !properties.hidePreview && (
              <div className="thumbnailContainer">
                {value.map((s3FileUrl) => {
                  const removeS3FileUrl = () => {
                    const valueWithoutS3FileUrl = value.filter((_value) => _value !== s3FileUrl);
                    setFieldValue(formField.id, valueWithoutS3FileUrl);
                  };

                  return (
                    <div key={s3FileUrl} className="p-relative">
                      <span
                        className="SrFormikFieldFileMultiRemoveFileButton button white small-admin"
                        onClick={removeS3FileUrl}
                      >
                        remove
                      </span>
                      <SrS3FileDisplay url={s3FileUrl} maxHeight="280px" />
                      {s3FileUrl && properties.requirePDFDocs && !isPDFFile(s3FileUrl) && (
                        <SrBox>
                          <SrText color="red" weight="bold">
                            The document provided is not a PDF. Please upload a PDF instead, if
                            possible.
                          </SrText>
                        </SrBox>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <SrMultiFileUploader
              totalFiles={value.length}
              maxFiles={properties.maxFiles}
              hidePreview={properties.hidePreview}
              accept={properties.fileAccept}
              value={properties.value}
              placeholder={properties.placeholder}
              filePath={properties.filePath}
              isPrivate={properties.isPrivate}
              uploadAs={properties.uploadAs}
              successMessage={properties.successMessage}
              replaceFields={properties.replaceFields}
              onFilesUploaded={(uploadResponses) => {
                const s3FileUrls = uploadResponses.map(({ s3FileUrl }) => s3FileUrl);
                const valueArray = Array.isArray(value) ? value : [];
                const newValue = properties.replaceFields
                  ? s3FileUrls
                  : [...valueArray, ...s3FileUrls];

                setFieldValue(formField.id, newValue);

                if (properties.onChange) {
                  properties.onChange(newValue);
                }
              }}
            />
          </div>
        );
        break;
      }
      case 'file_multi_upload_date': {
        fieldDOM = (
          <div>
            {value &&
              value.files &&
              Array.isArray(value.files) &&
              value.files.length > 0 &&
              !properties.hidePreview && (
                <div>
                  <SrBox
                    borderRadius="8px"
                    bgColor="blueLight800"
                    padding="16px"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    marginBottom="8px"
                  >
                    <SrText
                      font="greycliff"
                      color="blue300"
                      fontSize="16px"
                      weight="800"
                      decoration="underline"
                      display="block"
                      cursor="pointer"
                      onClick={properties.onViewDocument || emptyFunction}
                    >
                      View {`${pluralize('Document', value.files.length)}`} ({value.files?.length}{' '}
                      {pluralize('file', value.files.length)})
                    </SrText>
                    <SrText
                      font="greycliff"
                      color="blueGreycliff"
                      fontSize="10px"
                      weight="700"
                      display="block"
                      fontStyle="italic"
                    >
                      Uploaded&nbsp;
                      {moment(value.files[value?.length - 1]?.uploadDate).format('MMM D, YYYY')}
                    </SrText>
                    <SrText
                      font="greycliff"
                      color="red"
                      fontSize="14px"
                      weight="700"
                      decoration="underline"
                      display="block"
                      cursor="pointer"
                      onClick={() => {
                        if (properties.onDeleteDocument) {
                          return properties.onDeleteDocument();
                        }
                        setFieldValue(formField.id, []);
                      }}
                    >
                      Delete
                    </SrText>
                  </SrBox>
                </div>
              )}
            <SrMultiFileUploader
              maxHeight={properties.maxHeight || '85px'}
              totalFiles={value.length}
              maxFiles={properties.maxFiles}
              hidePreview={properties.hidePreview}
              accept={properties.fileAccept}
              value={properties.value}
              placeholder={properties.placeholder}
              filePath={properties.filePath}
              isPrivate={properties.isPrivate}
              uploadAs={properties.uploadAs}
              successMessage={properties.successMessage}
              replaceFields={properties.replaceFields}
              onFilesUploaded={(uploadResponses) => {
                const s3FileUrls = uploadResponses.map(({ s3FileUrl }) => s3FileUrl);
                const valueArray = Array.isArray(value.files) ? value.files : [];
                const newValue = {
                  uploadDate: moment().toISOString(),
                  files: properties.replaceFields ? s3FileUrls : [...valueArray, ...s3FileUrls],
                };

                setFieldValue(formField.id, newValue);

                if (properties.onChange) {
                  properties.onChange(newValue);
                }
              }}
            />
          </div>
        );
        break;
      }
      case 'autocomplete': {
        const textInputFieldAttributes = getTextInputFieldAttributes({
          formField,
          formikBag,
          userAgent,
        });
        // https://www.npmjs.com/package/react-autosuggest
        const valueForMatching = value ? value.trim().toLowerCase() : '';
        const nativeFilterFunc = (choice) => {
          const lowerCaseChoice = choice.toLowerCase();
          return (
            valueForMatching.length >= properties.minCharMatchCount ||
            (2 &&
              valueForMatching !== lowerCaseChoice &&
              lowerCaseChoice.indexOf(valueForMatching) === 0)
          );
        };
        const customFilterFunc = (choice) => {
          return properties.filterFunc({
            value: valueForMatching,
            choice: choice,
          });
        };

        const filterFunc =
          typeof properties.filterFunc === 'function' ? customFilterFunc : nativeFilterFunc;

        if (properties.onBlur) {
          textInputFieldAttributes.onBlur = properties.onBlur;
        }

        fieldDOM = (
          <Autosuggest
            suggestions={properties.choices.filter(filterFunc)}
            onSuggestionsFetchRequested={properties.onSuggestionsFetchRequested || emptyFunction}
            onSuggestionsClearRequested={() => {
              // Autosuggest complains if we don't pass in this empty function.
            }}
            onSuggestionSelected={(event, { suggestion: newValue }) => {
              setFieldValue(formField.id, newValue);
              if (properties.onSuggestionSelected) {
                properties.onSuggestionSelected({ newValue, formikBag });
              }
            }}
            shouldRenderSuggestions={properties.shouldRenderSuggestions}
            getSuggestionValue={(suggestion) => suggestion}
            renderSuggestion={(suggestion) => suggestion}
            inputProps={{
              ...textInputFieldAttributes,
              autoComplete: 'off',
              value: value || '',
              autoFocus: properties.autoFocus,
              onChange: (event, { newValue }) => {
                setFieldValue(formField.id, newValue);
                if (typeof textInputFieldAttributes?.onChange === 'function') {
                  textInputFieldAttributes.onChange({ newValue, formikBag });
                }
              },
            }}
            alwaysRenderSuggestions={properties.alwaysRenderSuggestions}
          />
        );
        break;
      }
      case 'star_rating': {
        fieldDOM = (
          <SrStarRating
            rate={value}
            onChange={(rate) => {
              setFieldValue(formField.id, rate);
              if (properties.onChange) {
                properties.onChange(rate);
              }
            }}
          />
        );
        break;
      }
      case 'tags': {
        fieldDOM = (
          <SrFormFieldTagList
            value={value}
            optionList={properties.choices}
            onChange={(newValue) => {
              setFieldValue(formField.id, newValue);
            }}
            justifyContent={properties.justifyContent}
          />
        );
        break;
      }
      case 'google_address_autocomplete': {
        fieldDOM = (
          <SrFormFieldGoogleAddressAutoComplete
            value={value}
            formField={formField}
            onSelectAddress={(address) => {
              if (!address) {
                setFieldTouched(formField.id, true, false);
                setFieldError(formField.id, 'Address is not valid');
                return;
              }
              setFieldValue(formField.id, address);
            }}
            onChange={(selectedAddress) => {
              setFieldValue(formField.id, selectedAddress);
            }}
            onBlur={(selectedAddress) => {
              if (value === '') {
                setFieldTouched(formField.id, true);
                return;
              }

              if (!selectedAddress) {
                setFieldTouched(formField.id, true, false);
                setFieldError(formField.id, 'Address is not valid');
              }
            }}
            textInputFieldAttributes={getTextInputFieldAttributes({
              formField,
              formikBag,
              userAgent,
            })}
          />
        );
        break;
      }
      case 'smarty_address_autocomplete': {
        fieldDOM = (
          <SrFormFieldSmartyAddressAutoComplete
            value={value}
            formField={formField}
            onSelectAddress={(address) => {
              if (!address) {
                setFieldTouched(formField.id, true, false);
                setFieldError(formField.id, 'Please select an address');
                return;
              }
              setFieldValue(formField.id, address);
            }}
            onChange={(selectedAddress) => {
              setFieldValue(formField.id, selectedAddress);
            }}
            onBlur={(selectedAddress) => {
              if (value === '') {
                setFieldTouched(formField.id, true);
                return;
              }
              if (!selectedAddress) {
                setFieldTouched(formField.id, true, false);
                setFieldError(formField.id, 'Please select an address');
              }
            }}
            textInputFieldAttributes={getTextInputFieldAttributes({
              formField,
              formikBag,
              userAgent,
            })}
          />
        );
        break;
      }
      case 'activation_auto_complete': {
        fieldDOM = (
          <SrFormFieldActivationAutoComplete
            value={value}
            formField={formField}
            onSelect={(newValue) => {
              setFieldValue(formField.id, newValue);
              if (this.el) {
                this.el.blur();
              }
            }}
            onSuggestionSelected={properties.onSuggestionSelected}
            setFieldValue={(newValue) => setFieldValue(formField.id, newValue)}
            textInputFieldAttributes={getTextInputFieldAttributes({
              formField,
              formikBag,
              userAgent,
            })}
          />
        );
        break;
      }
      case 'dom': {
        fieldDOM = properties?.dom || null;
        break;
      }
      case 'hidden': {
        // No DOM or logic for hidden fields.
        break;
      }
      default: {
        const error = new Error(`Unsupported field type: "${formField.type}".`);
        error.name = 'SrUnsupportedFormFieldTypeError';
        error.details = {
          formField,
        };
        throw error;
      }
    }

    const containerProps = {
      className: classnames(
        'fieldsContainer sr-input',
        `fieldType-${formField.type}`,
        properties.noMargin || formField.type === 'hidden' ? 'noMargin' : null,
        theme,
        fieldsContainerClassName,
        {
          hasError: formikBag && formikBag.errors[formField.id] && formikBag.touched[formField.id],
          active: showActiveClassName,
        },
        properties.dataDogMasked ? 'dd-privacy-mask-user-input' : null,
      ),
      id: `field-${formField.id}`,
      style: { margin },
    };

    if (properties.focusInputOnClickContainer) {
      containerProps.onClick = this._onClickContainer;
    }

    const isFormFieldTypeHidden = formField.type === 'hidden';

    const innerDOM = lodashCompact([
      formField.title && !isFormFieldTypeHidden && (
        <span
          key="title"
          className={classnames(
            'title',
            formField.titleSecondary ? 'reduceMargin' : null,
            formField.titleMultiline ? 'multiline' : null,
            formField.titleToggle ? 'titleWithToggle' : null,
            formField.titleClassName,
          )}
        >
          {formField.title}
          {formField.titleToggle && (
            <span className="titleToggle" onClick={formField.titleToggle.onClick}>
              {formField.titleToggle.text}
            </span>
          )}
        </span>
      ),
      formField.titleSecondary && !isFormFieldTypeHidden && (
        <span key="titleSecondary" className="titleSecondary">
          {formField.titleSecondary}
        </span>
      ),
      formField.label && !isFormFieldTypeHidden && (
        <span key="label" className="label">
          {formField.label}
        </span>
      ),
      formField.tooltipCopy && !isFormFieldTypeHidden && (
        <span style={{ paddingLeft: '5px' }}>
          <SrTooltip
            iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
            position={
              formField.tooltipPosition ? formField.tooltipPosition : SR_TOOLTIP_POSITION_LEFT
            }
            bgColor={formField.tooltipBgColor ? formField.tooltipBgColor : null}
            className={formField.tooltipClassName ? formField.tooltipClassName : null}
          >
            {formField.tooltipCopy}
          </SrTooltip>
        </span>
      ),
      formField.subtitle && !isFormFieldTypeHidden && (
        <div key="subtitle" className="fieldSubtitle">
          {formField.subtitle}
        </div>
      ),

      fieldDOM ? (
        <div key="fieldDom" className="value">
          {fieldDOM}
        </div>
      ) : null,
    ]);

    /* eslint-disable jsx-a11y/label-has-associated-control */
    return (
      <div {...containerProps} data-dd-privacy={formField.type === 'ssn' ? 'mask' : 'allow'}>
        {shouldWrapInLabel ? <label>{innerDOM}</label> : innerDOM}
        {formikBag && !isFormFieldTypeHidden && (
          <SrFormikErrorMessage id={formField.id} formikBag={formikBag} />
        )}
        {properties.widget}
      </div>
    );
    // eslint-enable jsx-a11y/label-has-associated-control
  }
}

SrFormikField.displayName = 'SrFormikField';

export default SrFormikField;
